import React, { useState, useCallback } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Tabs from "aldoo-ra/Tabs"
import DataTable from "aldoo-ra/DataTable"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import ObjectEditor from "aldoo-ra/CMS/field-editors/object"
import { showNotification } from "aldoo-ra/Notification"
import { PlusCircleIcon, TrashIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline"
import { CopyId, sortArg, confirmDelete, linkCell } from "./KPITiles"
import KPIDashboard from "./KPIDashboard"
import AssetEditor from "../AssetEditor"
import AssetSelectorDialog from "../AssetSelectorDialog"
import { AdCRUD, AssetCRUD } from "../api"

// Variables are stored as a flat { key: value } object on the ad and fed into
// asset {{placeholders}} at serve time (MailTemplates model). The CMS
// ObjectEditor works on a [{key,value}] array, so convert at the edges.
const objToRows = (o) => Object.entries(o || {}).map(([key, value]) => ({ key, value: String(value ?? "") }))
const rowsToObj = (rows) =>
  (Array.isArray(rows) ? rows : []).reduce((acc, r) => {
    const k = (r?.key || "").trim()
    if (k) acc[k] = r.value ?? ""
    return acc
  }, {})

/**
 * Ad level — KPIs + two tabs:
 *   Configuration: CTA url + the {{variable}} values (CMS object editor)
 *   Assets:        the ad's banners (HTML editor)
 */
const AdView = ({ account, isRemote, campaign, ad, onDeleted }) => {
  const [ctaUrl, setCtaUrl] = useState(ad.cta_url || "")
  const [varRows, setVarRows] = useState(objToRows(ad.variables))
  const [status, setStatus] = useState(ad.status)
  const [editorAsset, setEditorAsset] = useState(null) // null | {} (new) | {html,name} (loaded) | asset doc
  const [loadOpen, setLoadOpen] = useState(false)
  const [refresh, setRefresh] = useState(0)
  const [dateRange, setDateRange] = useState({ type: "preset", value: { days: 30 } })
  const [tab, setTab] = useState(isRemote ? "assets" : "config")

  const tabs = isRemote
    ? [{ value: "assets", label: "Assets" }]
    : [{ value: "config", label: "Configuration" }, { value: "assets", label: "Assets" }]

  const loadAssets = useCallback(async (params) => {
    const res = await AssetCRUD.Get({
      match: { ad: ad._id, deleted: { $ne: true } },
      page: params.page,
      itemsPerPage: params.itemsPerPage,
      sort: sortArg(params.sort),
    })
    return { data: res?.data || [], total: res?.totalItems || res?.total || 0 }
  }, [ad, refresh])

  const saveAd = async () => {
    const variables = rowsToObj(varRows)
    await AdCRUD.Update({ _id: ad._id, cta_url: ctaUrl, variables })
    ad.cta_url = ctaUrl
    ad.variables = variables
    showNotification({ message: "Ad saved", className: "bg-admin_success text-admin_text_inv" })
  }

  const toggleAd = async () => {
    const next = status === "active" ? "stopped" : "active"
    await AdCRUD.Update({ _id: ad._id, status: next })
    setStatus(next)
    ad.status = next
  }

  const deleteAd = () =>
    confirmDelete(ad.name, async () => {
      await AdCRUD.SoftDelete({ _id: ad._id })
      showNotification({ message: "Ad deleted", className: "bg-admin_success text-admin_text_inv" })
      onDeleted?.()
    })

  const deleteAsset = (asset) =>
    confirmDelete(asset.name, async () => {
      await AssetCRUD.SoftDelete({ _id: asset._id })
      showNotification({ message: "Asset deleted", className: "bg-admin_success text-admin_text_inv" })
      setRefresh((r) => r + 1)
    })

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Typography variant="admin_h3">{ad.name}</Typography>
          {/* the integration handle: <AldooAd id="..."/> needs only this */}
          <CopyId id={ad._id} />
        </div>
        <div className="flex items-center gap-2">
          <DateFilterDropdown
            showLabel={false}
            value={dateRange}
            onChange={setDateRange}
            variant="admin"
            localUserPreferencesKey="admin.ads.ad.dateFilter"
          />
          {!isRemote && (
            <>
              <Button variant="admin_secondary" onClick={toggleAd}>
                {status === "active" ? "Stop ad" : "Start ad"}
              </Button>
              <Button variant="admin_error" onClick={deleteAd}>
                <TrashIcon className="w-5 h-5 mr-1" /> Delete
              </Button>
            </>
          )}
        </div>
      </div>

      <KPIDashboard level="ad" id={ad._id} account={isRemote ? account._id : undefined} range={dateRange} />

      <Tabs variant="admin" tabs={tabs} value={tab} onChange={setTab} />

      {tab === "config" && !isRemote && (
        <div className="bg-paper border border-admin_bordered rounded-lg p-4 flex flex-col gap-4 max-w-3xl">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-admin_text">CTA URL</label>
            <span className="text-xs text-admin_text_secondary">
              Where a click ultimately lands — utm params are appended automatically.
            </span>
            <input
              className="border border-admin_bordered rounded-lg px-3 py-2 bg-transparent mt-1"
              value={ctaUrl}
              onChange={(e) => setCtaUrl(e.target.value)}
              placeholder="https://transfer.aldoo.com/"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-admin_text">Variables</label>
            <span className="text-xs text-admin_text_secondary">
              Key/value pairs substituted into asset {"{{placeholders}}"} at serve time.
            </span>
            <div className="mt-1">
              <ObjectEditor
                field={{ id: "variables", label: "Variables" }}
                value={ad.variables || {}}
                onChange={setVarRows}
                showInfo={false}
              />
            </div>
          </div>

          <div>
            <Button variant="admin_primary" onClick={saveAd}>Save ad</Button>
          </div>
        </div>
      )}

      {tab === "assets" && (
        <div className="flex flex-col gap-3">
          {!isRemote && (
            <div className="flex justify-end gap-2">
              <Button variant="admin_secondary" onClick={() => setLoadOpen(true)}>
                <ArrowDownTrayIcon className="w-5 h-5 mr-1" /> Load Asset
              </Button>
              <Button variant="admin_primary" onClick={() => setEditorAsset({})}>
                <PlusCircleIcon className="w-5 h-5 mr-1" /> New Asset
              </Button>
            </div>
          )}
          <DataTable
            key={`assets-${ad._id}-${refresh}`}
            apiMethod={loadAssets}
            itemsPerPage={25}
            onRowClick={(item) => { if (!isRemote) setEditorAsset(item) }}
            renders={[
              { id: "name", title: "Asset", sortable: true, render: linkCell },
              {
                id: "enabled", title: "Enabled", sortable: true,
                render: (v) => (
                  <span className={v ? "text-admin_success font-semibold" : "text-admin_text_secondary"}>
                    {v ? "yes" : "no"}
                  </span>
                ),
              },
              { id: "created", title: "Created", sortable: true, render: (v) => (v ? new Date(v).toLocaleDateString() : "") },
              ...(isRemote ? [] : [{
                id: "_delete", title: "",
                render: (_, asset) => (
                  <button
                    className="text-admin_error hover:opacity-70"
                    title="Delete asset"
                    onClick={(e) => { e.stopPropagation(); deleteAsset(asset) }}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                ),
              }]),
            ]}
          />
        </div>
      )}

      {editorAsset !== null && (
        <AssetEditor
          ad={ad}
          // full doc (has _id) → edit; {} or {name,html} (no _id) → new,
          // optionally pre-seeded from a loaded asset
          asset={editorAsset}
          onClose={() => setEditorAsset(null)}
          onSaved={() => { setEditorAsset(null); setRefresh((r) => r + 1) }}
        />
      )}

      {loadOpen && (
        <AssetSelectorDialog
          isOpen
          onClose={() => setLoadOpen(false)}
          onSelect={(asset) =>
            // copy the chosen asset's markup into a NEW asset under this ad
            setEditorAsset({ name: asset.name, html: asset.html, enabled: true })
          }
        />
      )}
    </div>
  )
}

export default AdView
